import { zodResolver } from '@hookform/resolvers/zod';
import { MessageType, useUpdateFrequencyCapMutation } from 'api/MessageTypeAPI';
import {
  MESSAGE_TYPE_FIELDS_TEXT,
  SCHEMA_ERROR_TEXT,
  maxIntValue,
} from 'modules/channels/constants';
import { useChannelsContext } from 'modules/channels/contexts';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { ApiError } from 'services/api';
import * as z from 'zod';
import { UPDATE_FREQUENCY_CAP_TEXT } from './constants';
import { FrequencyCapFormValues } from './types';
import { createFrequencyCapDefaultValues, createFrequencyCapPayload } from './utils';

const {
  VALUES: { CUSTOM, NONE },
} = MESSAGE_TYPE_FIELDS_TEXT;

const schema = z
  .object({
    frequencyCapOption: z.enum([CUSTOM, NONE]),
    numMessagesInPeriod: z.string(),
    period: z.string(),
  })
  .superRefine((values, ctx) => {
    const { frequencyCapOption, ...nums } = values;
    const isCustom = frequencyCapOption === CUSTOM;

    Object.keys(nums).forEach((val) => {
      const numsVal = nums[val as 'numMessagesInPeriod' | 'period'];

      // Ensure it is a number (ie. not `9e5`);
      const numbersOnlyRegex = /^[0-9]+$/;
      const isNumberOnly = numbersOnlyRegex.test(numsVal.toString());
      if (!isNumberOnly && isCustom) {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_string,
          message: `${SCHEMA_ERROR_TEXT.regex}`,
          path: [val],
          validation: 'regex',
        });
      }

      // Ensure number is positive
      const numsValInt = parseInt(numsVal, 10);
      if (numsValInt < 1 && isCustom) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 1,
          type: 'number',
          inclusive: true,
          message: `${SCHEMA_ERROR_TEXT.minValue}`,
          path: [val],
        });
      }

      // Ensure number is smaller than maximum allowed
      if (numsValInt > maxIntValue && isCustom) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 1,
          type: 'number',
          inclusive: true,
          message: `${SCHEMA_ERROR_TEXT.maxValue}`,
          path: [val],
        });
      }
    });
  });

type HookProps = {
  messageType: MessageType;
  onClose: () => void;
};

export const useUpdateFrequencyCapForm = ({ messageType, onClose }: HookProps) => {
  const dispatch = useDispatch();
  const { refetchMessageTypes } = useChannelsContext();

  const defaultValues = createFrequencyCapDefaultValues(messageType);

  const methods = useForm({
    defaultValues,
    mode: 'onSubmit',
    resolver: zodResolver(schema),
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    formState: { isValid },
  } = methods;

  const { mutate } = useUpdateFrequencyCapMutation({
    onSuccess: () => {
      onClose();
      dispatch(
        addPositiveToast(
          <>
            {UPDATE_FREQUENCY_CAP_TEXT.SUCCESS_MESSAGE} <strong>{messageType.name}</strong>
          </>,
        ),
      );
      refetchMessageTypes();
    },
    onError: (e: unknown) => {
      const { response } = e as ApiError;
      const errorText = `${UPDATE_FREQUENCY_CAP_TEXT.ERROR_MESSAGE} ${response?.data}.`;
      onClose();
      dispatch(addNegativeToastWithErrorId(errorText, 'useUpdateFrequencyCapMutation', e));
    },
  });

  const onValid: SubmitHandler<FrequencyCapFormValues> = (values) => {
    if (!isValid) return null;

    const payload = createFrequencyCapPayload(messageType, values);
    mutate(payload);
  };

  return { methods, onSubmit: handleSubmit(onValid) };
};
